import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TradingToast, useToast } from '@trading-ui/core';
import { useCallback, useEffect, useState } from 'react';

import { changeCompanyType, deleteCompany, getAllActiveCompanies } from '@/api/services/settings/moeve-actions.service';

import { QUERY_KEYS } from '@/api/config/query-keys';

import { parseBackendReason } from '@/utils/error.utils';

import type { ProcessedBackendError } from '@/interfaces';
import { useTranslation } from 'react-i18next';
import useSettingsStore from '../../store/useSettingsStore';

export const useActiveCompanies = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { setActiveCompanies, filterActiveCompanies } = useSettingsStore();

  const { t } = useTranslation();

  const [isChangingCompanyType, setIsChangingCompanyType] = useState(false);

  const {
    data: activeCompanies,
    isLoading,
    error,
    isRefetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.ACTIVE_COMPANIES],
    queryFn: getAllActiveCompanies,
  });

  useEffect(() => {
    if (error) {
      const errorMessage = parseBackendReason(error as Error | ProcessedBackendError);
      addToast(<TradingToast type="error" title="Error" message={errorMessage} />);
    }
  }, [error, addToast]);

  useEffect(() => {
    if (activeCompanies && activeCompanies.length > 0) {
      setActiveCompanies(activeCompanies);
      filterActiveCompanies();
    }
  }, [activeCompanies, setActiveCompanies, filterActiveCompanies]);

  const handleChangeCompanyType = useCallback(
    async (companyId: number, companyTypeId: number) => {
      setIsChangingCompanyType(true);
      try {
        await changeCompanyType(companyId, companyTypeId);

        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_COMPANIES] });

        addToast(<TradingToast message={t('services.activeCompanies.companyTypeChanged')} type="success" />);
      } catch (err) {
        const errorMessage = parseBackendReason(err as Error | ProcessedBackendError);
        addToast(<TradingToast title="Error" message={errorMessage} type="error" />);
      } finally {
        setIsChangingCompanyType(false);
      }
    },
    [queryClient, addToast, t]
  );

  const handleDeleteCompany = useCallback(
    async (companyId: number) => {
      try {
        await deleteCompany(companyId);

        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_COMPANIES] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BIOFLUX_MASTERS] });

        addToast(<TradingToast message={t('services.activeCompanies.companyDeleted')} type="success" />);
      } catch (err) {
        const errorMessage = parseBackendReason(err as Error | ProcessedBackendError);
        addToast(<TradingToast title="Error" message={errorMessage} type="error" />);
      }
    },
    [queryClient, addToast, t]
  );

  return {
    activeCompanies,
    isLoading,
    isRefetching,
    isChangingCompanyType,
    error,
    handleChangeCompanyType,
    handleDeleteCompany,
  };
};
